import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { ListOrdered, MessageCircleQuestionIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/ui/empty-state";
import { SettingsLayout } from "@/components/ui/settings-layout";
import { useCampaignStore } from "@/stores";
import { useEveActionsStore } from "@/stores/eve/eveActionsStore";
import { EvePipelinePhase } from "@/types/eve";
import { evePhaseOrder, pipelineGroups, sameForAllTiers } from "@/lib/eve";
import { useAmyDeepLink } from "@/hooks/use-amy-deep-link";
import { PipelineNav, PipelineNavGroup } from "../pipeline/PipelineNav";
import { OverviewSection } from "../pipeline/OverviewSection";
import { PhaseSection } from "../pipeline/PhaseSection";
import { CopyTierDialog, ShareTierDialog, TierMenu } from "../pipeline/TierActions";
import { PhaseAction, PhaseActionDialog } from "../pipeline/PhaseActions";
import { tierOfferLabel } from "../pipeline/shared";

/**
 * Editable pipeline for eve campaigns — SettingsTab-literal master/detail:
 * a sticky left nav (Overview + the phases, URL-driven via the stages/:phase
 * route param) and Frame-based editor sections on the right.
 *
 * All writes ride the amy action routes (POST /eve/v1/actions/*), which run
 * the SAME validated executors as Amy's chat tools — dashboard code never
 * $sets eve_* fields (AGENTS.md contract). Template, custom-step, and
 * event-hold config stays chat-only and renders read-only with deep links.
 *
 * Per-tier pipelines (2026-10-16): a campaign has ONE pipeline PER
 * PARTNERSHIP TIER. The editor's mode is DERIVED from the data — no stored
 * flag, so it can never disagree with the database:
 *  - single-tier campaign: exactly the plain editor (no tier UI at all);
 *  - multi-tier, every tier identical: the nav shows ONE "All N tiers" group
 *    whose edits reach every tier (saves omit `tiers`);
 *  - per-tier view (the tiers differ, or the user split them): the nav shows
 *    one group per tier, and a phase opened from a group edits that tier
 *    only (saves send `tiers: [tier]`). The tier rides the `?tier=` search
 *    param (the stages/:phase route is unchanged), which also keeps a split
 *    of still-identical tiers open across navigation.
 * Splitting and merging live on the Overview's "Different per tier" switch
 * and on each group's ⋯ menu (TierActions).
 */
const byOrder = (phases: EvePipelinePhase[]) => [...phases].sort((a, b) => evePhaseOrder(a) - evePhaseOrder(b));

export default function PipelineTab() {
  const { campaign, campaignId } = useCampaignStore();
  const { phase: phaseParam } = useParams<{ phase: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getToken, orgId } = useAuth();
  const { updateCampaignPhases, isSavingConfig } = useEveActionsStore();
  const openAmy = useAmyDeepLink();

  const groups = useMemo(() => pipelineGroups(campaign), [campaign]);
  const identical = useMemo(() => sameForAllTiers(campaign), [campaign]);
  const multiTier = groups.length > 1;
  // The user split still-identical tiers (a pure view choice — nothing is
  // written until a per-tier save makes them differ).
  const [perTierSwitch, setPerTierSwitch] = useState(false);
  const tierParam = Number(searchParams.get("tier"));
  const perTier = multiTier && (!identical || perTierSwitch || searchParams.has("tier"));
  // The tier the open section belongs to; null = the shared pipeline.
  const selectedTier = perTier ? (groups.some((g) => g.tier === tierParam) ? tierParam : groups[0].tier) : null;

  // Dialogs: the tier being copied from / preselected as the shared pipeline.
  const [copyFrom, setCopyFrom] = useState<number | null>(null);
  const [shareFrom, setShareFrom] = useState<number | null>(null);
  // A phase's Reset to defaults / Delete, picked from its ⋯ menu in the nav.
  const [phaseAction, setPhaseAction] = useState<PhaseAction | null>(null);

  const offerOf = (tier: number) =>
    tierOfferLabel(campaign?.partnership?.tiers?.find((t) => t.order === tier), campaign?.currency);
  const tierHeading = (tier: number) => (offerOf(tier) ? `Tier ${tier} · ${offerOf(tier)}` : `Tier ${tier}`);
  const allTiers = `All ${groups.length} tiers`;

  // Relative NavLink targets: the index route resolves children ("contract"),
  // the :phase route resolves siblings ("../contract").
  const linkTo = (slug: string, tier: number | null) =>
    `${phaseParam ? `../${slug}` : slug}${tier != null ? `?tier=${tier}` : ""}`;

  const writeTierParam = (tier: number | null) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tier == null) next.delete("tier");
        else next.set("tier", String(tier));
        return next;
      },
      { replace: true }
    );

  const collapse = () => {
    setPerTierSwitch(false);
    writeTierParam(null);
  };

  const setPerTier = (on: boolean) => {
    if (on) {
      setPerTierSwitch(true);
      writeTierParam(groups[0].tier);
    } else if (identical) {
      collapse();
    } else {
      // The tiers differ: one of them has to become the shared pipeline — ask which.
      setShareFrom(selectedTier ?? groups[0].tier);
    }
  };

  const shareTier = (tier: number) => (identical ? collapse() : setShareFrom(tier));

  const menuFor = (tier: number | null) => (
    <TierMenu
      tier={tier}
      heading={tier == null ? allTiers : tierHeading(tier)}
      onSplit={() => setPerTier(true)}
      onCopy={setCopyFrom}
      onUseForAll={shareTier}
      disabled={isSavingConfig}
    />
  );

  const navGroups: PipelineNavGroup[] = !multiTier
    ? [{ tier: null, pipeline: byOrder(groups[0].phases), targetTiers: null }]
    : perTier
      ? groups.map((g) => ({
          tier: g.tier,
          title: `Tier ${g.tier}`,
          subtitle: offerOf(g.tier),
          pipeline: byOrder(g.phases),
          targetTiers: [g.tier],
          menu: menuFor(g.tier),
        }))
      : [{ tier: null, title: allTiers, pipeline: byOrder(groups[0].phases), targetTiers: null, menu: menuFor(null) }];

  const selectedGroup = navGroups.find((g) => g.tier === selectedTier) ?? navGroups[0];
  const pipeline = selectedGroup.pipeline;
  const selectedPhase = phaseParam ? pipeline.find((p) => p.key === phaseParam) : undefined;
  const selected = selectedPhase ? selectedPhase.key : "overview";

  const handleReorder = async (group: PipelineNavGroup, key: string, direction: "up" | "down") => {
    const list = group.pipeline;
    const idx = list.findIndex((p) => p.key === key);
    const j = direction === "up" ? idx - 1 : idx + 1;
    if (idx === -1 || j < 0 || j >= list.length) return;
    const a = evePhaseOrder(list[idx]);
    const b = evePhaseOrder(list[j]);
    // Swap the two effective orders; on a tie renumber the whole list by 10s
    // so relative positions stay unambiguous.
    const patches =
      a !== b
        ? [
            { key: list[idx].key, order: b },
            { key: list[j].key, order: a },
          ]
        : (() => {
            const next = [...list];
            [next[idx], next[j]] = [next[j], next[idx]];
            return next.map((p, i) => ({ key: p.key, order: (i + 1) * 10 }));
          })();
    const token = await getToken();
    if (!token || !campaignId || !orgId) return console.error("No token found");
    await updateCampaignPhases(campaignId, patches, orgId, token, { tiers: group.targetTiers });
  };

  const scope = !multiTier
    ? undefined
    : selectedTier != null
      ? `${tierHeading(selectedTier)} — changes here apply to this tier only.`
      : `Shared by ${allTiers.toLowerCase()} — changes here apply to every tier.`;

  // The shared settings layout (scroll, fade, section transition); the
  // tier dialogs are portals, so they sit outside the animated section.
  return (
    <>
      <SettingsLayout
        sectionKey={`${selectedTier}:${selected}`}
        nav={
          <PipelineNav
            groups={navGroups}
            selected={{ section: selected, tier: selectedTier }}
            linkTo={linkTo}
            onReorder={handleReorder}
            onPhaseAction={(group, phase, kind) =>
              setPhaseAction({ kind, phase, pipeline: group.pipeline, targetTiers: group.targetTiers, tier: group.tier })
            }
            reordering={isSavingConfig}
            onCreated={(key, tier) => navigate(linkTo(key, tier))}
          />
        }
      >
        {selected === "overview" ? (
          <>
            <OverviewSection
              pipeline={pipeline}
              tiers={
                multiTier
                  ? {
                      count: groups.length,
                      perTier,
                      onPerTierChange: setPerTier,
                      flows: groups.map((g) => ({
                        tier: g.tier,
                        offer: offerOf(g.tier),
                        pipeline: byOrder(g.phases),
                        menu: menuFor(g.tier),
                      })),
                    }
                  : undefined
              }
            />
            {!perTier && pipeline.length === 0 && (
              <EmptyState
                icon={<ListOrdered />}
                text="No pipeline phases configured. Ask Amy to add contract, script, draft, posting or payment steps."
                className="h-64"
              />
            )}
          </>
        ) : (
          // Keyed by tier too: switching tiers remounts the editor so a
          // half-edited frame never carries over to another tier's values.
          <PhaseSection
            key={`${selectedTier}:${selected}`}
            phase={selectedPhase!}
            pipeline={pipeline}
            targetTiers={selectedGroup.targetTiers}
            scope={scope}
          />
        )}
        <div className="flex justify-start">
          <Button
            variant="secondary"
            size="sm"
            className="w-fit"
            onClick={() =>
              openAmy(
                `Show me the pipeline phases for campaign "${campaign?.campaign_name ?? ""}" (campaign_id ${campaign?._id})${
                  selectedTier != null ? ` — tier ${selectedTier} in particular` : ""
                } — I may want to change them.`
              )
            }
          >
            <MessageCircleQuestionIcon /> Edit with Amy
          </Button>
        </div>
      </SettingsLayout>
      {campaign && multiTier && (
        <>
          <CopyTierDialog campaign={campaign} groups={groups} from={copyFrom} onClose={() => setCopyFrom(null)} />
          <ShareTierDialog
            campaign={campaign}
            groups={groups}
            source={shareFrom}
            onClose={() => setShareFrom(null)}
            onShared={collapse}
          />
        </>
      )}
      <PhaseActionDialog
        action={phaseAction}
        onClose={() => setPhaseAction(null)}
        onDeleted={(done) => {
          // The open section was the deleted phase: fall back to Overview.
          if (done.phase.key === selected && done.tier === selectedTier) navigate(linkTo("overview", selectedTier));
        }}
      />
    </>
  );
}
